import React, { useState, useEffect, useRef } from 'react';
import './AnimatedNumber.scss';

export interface AnimatedNumberProps {
  value: number;
  duration?: number;
  prefix?: string;
  suffix?: string;
  decimals?: number;
  separator?: string;
  easingFunction?: 'ease-out' | 'ease-in' | 'ease-in-out' | 'linear';
  className?: string;
  color?: string; // text color
}

const AnimatedNumber: React.FC<AnimatedNumberProps> = ({
  value,
  duration = 1000,
  prefix = '',
  suffix = '',
  decimals = 0,
  separator = ',',
  easingFunction = 'ease-out',
  className = '',
  color = 'black',
}) => {
  const [displayValue, setDisplayValue] = useState(0);
  const animationRef = useRef<number>();
  const startTimeRef = useRef<number>();
  const startValueRef = useRef<number>(0);
  const hasAnimatedRef = useRef<boolean>(false);


  // Format number with separators and decimals
  const formatNumber = (num: number): string => {
    const options: Intl.NumberFormatOptions = {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
      useGrouping: true,
    };

    let formatted = num.toLocaleString('en-US', options);

    // Replace default comma with custom separator if needed
    if (separator !== ',') {
      formatted = formatted.replace(/,/g, separator);
    }

    return `${prefix}${formatted}${suffix}`;
  };


  // Effect to handle value changes
  useEffect(() => {

    // Easing functions
    const easing = {
      'ease-out': (t: number) => 1 - Math.pow(1 - t, 3),
      'ease-in': (t: number) => t * t * t,
      'ease-in-out': (t: number) => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2,
      'linear': (t: number) => t,
    };

    // Animation function using requestAnimationFrame,called every 16ms by browser

    const animate = (currentTime: number) => {
      if (!startTimeRef.current) {
        startTimeRef.current = currentTime;
      }

      const elapsed = currentTime - startTimeRef.current;
      const progress = Math.min(elapsed / duration, 1);

      // Apply easing function
      const easedProgress = easing[easingFunction](progress);

      // Calculate current value
      const currentValue = startValueRef.current + (value - startValueRef.current) * easedProgress;
      setDisplayValue(currentValue);

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        setDisplayValue(value);
        hasAnimatedRef.current = true;
      }
    };

    // Start animation
    const startAnimation = (fromValue = 0) => {
      startValueRef.current = fromValue;
      startTimeRef.current = undefined;
      animationRef.current = requestAnimationFrame(animate);
    };

    // Check for reduced motion preference
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion) {
      setDisplayValue(value);
      hasAnimatedRef.current = true;
      return;
    }

    // Cancel any existing animation
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }

    // Always animate from 0 on mount or value change
    startAnimation(0);
    hasAnimatedRef.current = true;

    // Cleanup function
    // eslint-disable-next-line consistent-return
    return (): void => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [duration, easingFunction, value]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  return (
    <span
      className={`animated-number ${className}`}
      aria-live="polite"
      aria-label={formatNumber(value)}
      style={{ color }}
    >
      {formatNumber(displayValue)}
    </span>
  );
};

export default AnimatedNumber;
